import * as THREE from "three";

import { Vector2 } from "../../common/vector2.js";
import { Renderer } from "../../core/renderer.js";
import { SceneDimmer } from "../../core/scene_dimmer.js";
import { Game } from "../../game.js";
import { ImgID } from "../../image_loader/img_id.js";
import { BasicEventsController } from "../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { PillProgressFill } from "../common_ui_elements/pill_progress_fill.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import {
  BuyBoosterType,
  getBoosterTextVariableParams,
  getDefaultBoosterEffectValue,
} from "../dialog/buy_booster_types.js";
import { DialogManager } from "../dialog/dialog_manager.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { resolveUiBackend } from "../dom_backend/dom_backend.js";
import { createNullCanvasContext, isNullCanvasContext } from "../dom_backend/null_canvas_context.js";
import { startDomSampledAnimation } from "../dom_backend/dom_animations.js";

class BoosterHighlightRoot extends UIElementHolder
{
  public update( now: number ): void
  {
    this.updateBasic( now );
  }
}

export interface BoosterUIHighlight
{
  canvas: HTMLCanvasElement;
  client_rect: { x: number, y: number, width: number, height: number };
  dom_root?: HTMLElement;
}

export interface BoosterHighlightParams
{
  booster_type: BuyBoosterType;
  effect_value?: number;
  on_pointer_down: ( client_x: number, client_y: number ) => boolean;
  on_pointer_move: ( client_x: number, client_y: number ) => void;
  on_pointer_up: ( client_x: number, client_y: number ) => boolean;
  on_first_shot: () => void;
  on_cancel_booster: () => void;
  on_cancel_aim: () => void;
  on_close: () => void;
  on_scene_dimming: ( active: boolean ) => void;
  on_successful_shot?: () => void;
  aim_tutorial_target?: { client_x: number, client_y: number };
  ui_highlights: BoosterUIHighlight[];
}

interface BoosterHighlightTextConfig
{
  title_key: string;
  description_key: string;
}

const BOOSTER_TEXT_CONFIG: Record<BuyBoosterType, BoosterHighlightTextConfig> = {
  [BuyBoosterType.Firework]: {
    title_key: "BOOSTER.FIREWORK",
    description_key: "DESCRIPTION.FIREWORK",
  },
  [BuyBoosterType.FiveBalls]: {
    title_key: "BOOSTER.FIVE_BALLS",
    description_key: "DESCRIPTION.FIVE_BALLS",
  },
  [BuyBoosterType.BigBall]: {
    title_key: "BOOSTER.BIG_BALL",
    description_key: "DESCRIPTION.BIG_BALL",
  },
  [BuyBoosterType.InfiniteShots]: {
    title_key: "BOOSTER.INFINITE_SHOTS",
    description_key: "DESCRIPTION.INFINITE_SHOTS",
  },
};

/** Owns the complete booster-highlight presentation, input and lifecycle. */
export class BoosterHighlightRenderer
{
  public static readonly BACKGROUND_OPACITY = 0.78;  private static readonly TITLE_OUTLINE_COLOR = "#4150D5";
  private static readonly INFINITE_GRADIENT_OPACITY = 0.72;
  private static readonly INFINITE_GRADIENT_HEIGHT = 650;
  private static readonly CURSOR_PULSE_SCALE = 0.8;
  private static readonly CURSOR_PULSE_DURATION_MS = 650;
  private static readonly BOOSTER_BUMP_SCALE = 0.12;
  private static readonly BOOSTER_BUMP_DURATION_MS = 180;
  private static readonly PROGRESS_BASE_ART_H = 60;
  private static readonly PROGRESS_BASE_RIM = 5;
  private static readonly PROGRESS_BASE_DESIGN_H = 54;
  private static readonly PROGRESS_FILL_DESIGN_H = 40;

  private static text_canvas: HTMLCanvasElement | null = null;
  private static text_ctx: CanvasRenderingContext2D | null = null;
  private static ui_ctx: CanvasRenderingContext2D | null = null;
  private static dom_root: BoosterHighlightRoot | null = null;
  private static dom_progress_host: UIElementHolder | null = null;
  private static dom_bump_anims: Animation[] = [];
  private static dom_cursor_anim: Animation | null = null;
  private static progress_canvas: HTMLCanvasElement | null = null;
  private static progress_ctx: CanvasRenderingContext2D | null = null;
  private static progress_origin = { x: 0, y: 0, width: 0, height: 0 };
  private static events_controller: BasicEventsController | null = null;

  private static title: UITextElement | null = null;
  private static description: UITextElement | null = null;
  private static standard_description: UITextElement | null = null;
  private static infinite_description: UITextElement | null = null;
  private static progress_base: UINineSliceElement | null = null;
  private static progress_fill: PillProgressFill | null = null;
  private static aim_tutorial_cursor: UIImage | null = null;
  private static aim_cursor_rest_pos: Vector2 | null = null;

  private static params: BoosterHighlightParams | null = null;
  private static pointer_active = false;
  private static countdown_started = false;
  private static duration_started_at = 0;
  private static booster_bump_started_at: number | null = null;
  private static hide_after_booster_bump = false;
  private static closing_bump_timeout: number | null = null;
  private static animation_frame: number | null = null;

  private static ui_dirty = true;
  private static last_game_ui_revision = -1;
  private static last_layout_key = "";
  private static diag = { ui: 0, progress: 0, frames: 0 };

  private static legacyUiEveryFrame(): boolean
  {
    return localStorage.getItem( "perf_bh_text_every_frame" ) === "1";
  }

  public static diagnostics(): Record<string, unknown>
  {
    return {
      shown: this.params !== null,
      scene_dimmer_active: SceneDimmer.isActive(),
      dim_opacity: SceneDimmer.getOpacity(),
      legacy_ui_every_frame: this.legacyUiEveryFrame(),
      countdown: this.countdown_started,
      frames: this.diag.frames,
      ui_repaints: this.diag.ui,
      progress_repaints: this.diag.progress,
    };
  }

  public static resetDiagnostics(): void
  {
    this.diag = { ui: 0, progress: 0, frames: 0 };
  }

  public static markUIDirty(): void
  {
    this.ui_dirty = true;
  }

  private static startBoosterBump( hide_after: boolean ): void
  {
    if ( this.closing_bump_timeout !== null )
    {
      window.clearTimeout( this.closing_bump_timeout );
      this.closing_bump_timeout = null;
    }

    this.booster_bump_started_at = performance.now();
    this.hide_after_booster_bump = hide_after;
    this.startDomBump();

    if ( hide_after )
    {
      this.closing_bump_timeout = window.setTimeout( () =>
      {
        this.closing_bump_timeout = null;
        if ( !this.params || !this.hide_after_booster_bump )
          return;

        this.hide();
      }, this.BOOSTER_BUMP_DURATION_MS );
    }
  }

  public static show( params: BoosterHighlightParams ): void
  {
    if ( this.params )
      this.hide();

    this.ensureRenderer();
    this.params = params;
    this.pointer_active = false;
    this.countdown_started = false;
    this.duration_started_at = 0;
    this.ui_dirty = true;
    this.last_game_ui_revision = -1;
    this.startBoosterBump( false );
    this.setBoosterText( params.booster_type, params.effect_value );
    this.syncLayout();

    SceneDimmer.disable();
    this.syncDimmingMode();
    this.text_canvas!.style.display = "block";
    this.text_canvas!.style.pointerEvents = "auto";
    this.progress_canvas!.style.display = this.usesProgressBar() && !this.dom_root ? "block" : "none";
    // The fill follows the countdown every frame, so a countdown cut short (level won mid-way)
    // leaves nothing behind: a new show always starts full.
    this.progress_fill?.setShare( 1 );
    if ( this.dom_progress_host )
      this.dom_progress_host.getDomNode().style.display = this.usesProgressBar() ? "" : "none";
    this.configureAimTutorialCursor();
    this.events_controller!.enable();

    this.renderFrame();
  }

  public static hide(): void
  {
    const params = this.params;
    this.params = null;
    this.pointer_active = false;
    this.booster_bump_started_at = null;
    this.hide_after_booster_bump = false;

    if ( this.closing_bump_timeout !== null )
    {
      window.clearTimeout( this.closing_bump_timeout );
      this.closing_bump_timeout = null;
    }

    if ( this.animation_frame !== null )
    {
      cancelAnimationFrame( this.animation_frame );
      this.animation_frame = null;
    }

    this.events_controller?.disable();
    this.hideAimTutorialCursor();
    if ( this.text_canvas )
    {
      this.text_canvas.style.pointerEvents = "none";
      this.text_canvas.style.display = "none";
    }
    SceneDimmer.disable();

    if ( this.progress_canvas && this.progress_ctx )
    {
      this.progress_canvas.style.display = "none";
      this.progress_ctx.clearRect( 0, 0, this.progress_origin.width, this.progress_origin.height );
    }

    if ( this.text_ctx )
      this.text_ctx.clearRect( 0, 0, Game.screenW(), Game.screenH() );
    this.clearDomHighlights();
    for ( const anim of this.dom_bump_anims )
      anim.cancel();
    this.dom_bump_anims = [];
    this.dom_cursor_anim?.cancel();
    this.dom_cursor_anim = null;
    if ( this.dom_root )
      this.dom_root.getDomNode().style.display = "none";

    if ( params )
    {
      try
      {
        params.on_cancel_aim();
      }
      finally
      {
        params.on_close();
      }
    }
  }

  public static isShown(): boolean
  {
    return this.params !== null;
  }

  private static ensureRenderer(): void
  {
    if ( this.text_canvas )
      return;

    this.text_canvas = document.createElement( "canvas" );
    this.text_canvas.id = "boosterHighlightTextCanvas";
    this.text_canvas.style.position = "absolute";
    this.text_canvas.style.pointerEvents = "none";
    this.text_canvas.style.touchAction = "none";
    this.text_canvas.style.zIndex = "125";
    this.text_canvas.style.display = "none";
    this.text_ctx = this.text_canvas.getContext( "2d" );
    if ( !this.text_ctx )
      throw new Error( "Could not create the booster-highlight text context" );

    document.body.appendChild( this.text_canvas );
    this.ui_ctx = resolveUiBackend( "game" ) === "dom" ? createNullCanvasContext( this.text_ctx ) : this.text_ctx;

    this.progress_canvas = document.createElement( "canvas" );
    this.progress_canvas.id = "boosterHighlightProgressCanvas";
    this.progress_canvas.style.position = "absolute";
    this.progress_canvas.style.pointerEvents = "none";
    this.progress_canvas.style.zIndex = "126";
    this.progress_canvas.style.display = "none";
    this.progress_ctx = this.progress_canvas.getContext( "2d" );
    if ( !this.progress_ctx )
      throw new Error( "Could not create the booster-highlight progress context" );

    document.body.appendChild( this.progress_canvas );
    this.createTextElements();
    this.createEventsController();
  }

  private static createEventsController(): void
  {
    this.events_controller = new BasicEventsController( this.text_canvas! );
    this.events_controller.init(
      point => this.handlePointerDown( point.x, point.y ),
      point => this.handlePointerUp( point.x, point.y ),
      point => this.handlePointerMove( point.x, point.y )
    );
    this.events_controller.disable();
  }

  private static toClientPoint( local_x: number, local_y: number ): { x: number, y: number }
  {
    const bounds = this.text_canvas!.getBoundingClientRect();
    return { x: bounds.left + local_x, y: bounds.top + local_y };
  }

  private static handlePointerDown( local_x: number, local_y: number ): void
  {
    if ( !this.params )
      return;

    const client = this.toClientPoint( local_x, local_y );
    const inside_booster = this.isPointInsideBoosterHighlight( client.x, client.y );
    if ( inside_booster )
    {
      if ( this.hide_after_booster_bump )
        return;

      if ( !this.countdown_started )
      {
        try
        {
          this.params.on_cancel_booster();
        }
        finally
        {
          this.startBoosterBump( true );
        }
      }
      return;
    }

    this.pointer_active = this.params.on_pointer_down( client.x, client.y );
  }

  private static isPointInsideBoosterHighlight( client_x: number, client_y: number ): boolean
  {
    if ( !this.params )
      return false;

    return this.params.ui_highlights.some( highlight =>
    {
      const rect = highlight.client_rect;
      return client_x >= rect.x
        && client_x <= rect.x + rect.width
        && client_y >= rect.y
        && client_y <= rect.y + rect.height;
    } );
  }

  private static handlePointerMove( local_x: number, local_y: number ): void
  {
    if ( !this.params || !this.pointer_active )
      return;

    const client = this.toClientPoint( local_x, local_y );
    this.params.on_pointer_move( client.x, client.y );
  }

  private static handlePointerUp( local_x: number, local_y: number ): void
  {
    if ( !this.params || !this.pointer_active )
      return;

    this.pointer_active = false;
    const client = this.toClientPoint( local_x, local_y );
    const fired = this.params.on_pointer_up( client.x, client.y );
    if ( !fired )
      return;

    this.params.on_successful_shot?.();

    if ( this.params.booster_type === BuyBoosterType.InfiniteShots )
    {
      if ( !this.countdown_started )
      {
        this.countdown_started = true;
        this.duration_started_at = performance.now();
        this.params.on_first_shot();
        this.syncDimmingMode();
        this.ui_dirty = true;
      }
      return;
    }

    this.hide();
  }

  private static createTextElements(): void
  {
    const ctx = this.ui_ctx!;
    if ( isNullCanvasContext( ctx ) )
    {
      this.dom_root = new BoosterHighlightRoot( ctx, 0, 0, Game.screenW(), Game.screenH(), false );
      this.dom_root.setClickable( false );
      this.dom_root.mountDomHost( this.text_canvas! );
    }
    const title_w = LayoutScaler.widthFromDesign( 900 );
    this.title = new UITextElement(
      ctx,
      Game.safeAreaLeft() + ( Game.getContentWidth() - title_w ) / 2,
      Game.safeAreaTop() + LayoutScaler.yFromDesign( 119 ),
      title_w,
      LayoutScaler.heightFromDesign( 109 ),
      {
        font_size: LayoutScaler.textSizeFromDesign( 80 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 16 ),
        outline_color: this.TITLE_OUTLINE_COLOR,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 48 ),
        auto_wrap: true,
      }
    );

    this.standard_description = this.createDescriptionElement( 245, true );
    this.infinite_description = this.createDescriptionElement( 205, false );
    this.description = this.standard_description;
    if ( this.dom_root )
    {
      this.dom_root.addChild( this.title );
      this.dom_root.addChild( this.standard_description );
      this.dom_root.addChild( this.infinite_description );
    }

    const progress_w = LayoutScaler.widthFromDesign( 726 );
    const progress_h = LayoutScaler.heightFromDesign( 54 );
    const progress_pad = LayoutScaler.heightFromDesign( 24 );
    this.progress_origin = {
      x: Math.round( Game.safeAreaLeft() + ( Game.getContentWidth() - progress_w ) / 2 ) - progress_pad,
      y: Math.round( Game.safeAreaTop() + LayoutScaler.yFromDesign( 323 ) ) - progress_pad,
      width: progress_w + progress_pad * 2,
      height: progress_h + progress_pad * 2,
    };
    this.createProgressBar( this.dom_root ? ctx : this.progress_ctx!, progress_pad, progress_w, progress_h );
    if ( this.dom_root )
    {
      this.dom_progress_host = new UIElementHolder( ctx, this.progress_origin.x, this.progress_origin.y, this.progress_origin.width, this.progress_origin.height, false );
      this.dom_progress_host.setClickable( false );
      this.dom_progress_host.addChild( this.progress_base! );
      this.dom_progress_host.addChild( this.progress_fill! );
      this.dom_root.addChild( this.dom_progress_host );
    }

    const cursor_w = LayoutScaler.widthFromDesign( 150 );
    const cursor_h = cursor_w * ( 183 / 159 );
    this.aim_tutorial_cursor = new UIImage(
      ctx,
      0,
      0,
      cursor_w,
      cursor_h,
      ImgID.IconTutorialCursor
    );
    this.aim_tutorial_cursor.setClickable( false );
    this.aim_tutorial_cursor.setOpacityImmediate( 0 );
    if ( this.dom_root )
      this.dom_root.addChild( this.aim_tutorial_cursor );
  }

  /**
   * Base pill and the fill window inside its dark track, placed like the Daily Tasks fill:
   * edges snapped to device px, the track taken from the base art (110x60, 5px rim) and
   * one whole inset on every side, so the fill's rounded ends stay concentric with the track's.
   */
  private static createProgressBar( ctx: CanvasRenderingContext2D, pad: number, width: number, height: number ): void
  {
    const dpr = window.devicePixelRatio || 1;
    const base_left = Math.round( pad * dpr );
    const base_top = Math.round( pad * dpr );
    const base_right = Math.round( ( pad + width ) * dpr );
    const base_bottom = Math.round( ( pad + height ) * dpr );
    const base_h = base_bottom - base_top;
    const rim = Math.max( 1, Math.round( base_h * this.PROGRESS_BASE_RIM / this.PROGRESS_BASE_ART_H ) );
    const track_h = base_h - rim * 2;
    const inset = Math.max( 1, Math.round( ( track_h - base_h * this.PROGRESS_FILL_DESIGN_H / this.PROGRESS_BASE_DESIGN_H ) / 2 ) );

    const base_h_px = base_h / dpr;
    // The caps keep the art's proportions, so the track's ends are true semicircles.
    this.progress_base = new UINineSliceElement(
      ctx,
      base_left / dpr,
      base_top / dpr,
      ( base_right - base_left ) / dpr,
      base_h_px,
      ImgID.UI_GameBoosterProgressBase,
      "booster_pb_base",
      {
        top_left: { width: base_h_px * 54 / this.PROGRESS_BASE_ART_H, height: 0 },
        bottom_right: { width: base_h_px * 55 / this.PROGRESS_BASE_ART_H, height: 0 },
      }
    );
    this.progress_base.setClickable( false );

    const fill_offset = rim + inset;
    this.progress_fill = new PillProgressFill(
      ctx,
      ( base_left + fill_offset ) / dpr,
      ( base_top + fill_offset ) / dpr,
      ( base_right - base_left - fill_offset * 2 ) / dpr,
      ( base_h - fill_offset * 2 ) / dpr,
      ImgID.UI_GameBoosterProgressFill,
      "booster_pb_fill",
      1
    );
    this.progress_fill.setClickable( false );
  }

  private static createDescriptionElement( design_y: number, auto_wrap: boolean ): UITextElement
  {
    const description_w = LayoutScaler.widthFromDesign( 760 );
    return new UITextElement(
      this.ui_ctx!,
      Game.safeAreaLeft() + ( Game.getContentWidth() - description_w ) / 2,
      Game.safeAreaTop() + LayoutScaler.yFromDesign( design_y ),
      description_w,
      LayoutScaler.heightFromDesign( 105 ),
      {
        font_size: LayoutScaler.textSizeFromDesign( 46 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 32 ),
        line_height: auto_wrap ? 0.95 : 1,
        auto_wrap,
        ...TextStyleScheme.buttonShadow(),
      }
    );
  }

  private static setBoosterText(
    booster_type: BuyBoosterType,
    effect_value?: number
  ): void
  {
    const config = BOOSTER_TEXT_CONFIG[booster_type];
    const text_params = getBoosterTextVariableParams( booster_type, effect_value );
    this.title!.setTextVariableParams( text_params );
    this.title!.setLocalizationKey( config.title_key );
    this.description = booster_type === BuyBoosterType.InfiniteShots
      ? this.infinite_description
      : this.standard_description;
    this.description!.setTextVariableParams( text_params );
    this.description!.setLocalizationKey( config.description_key );
  }

  private static getInfiniteShotsDurationMs(): number
  {
    const seconds = this.params?.effect_value
      ?? getDefaultBoosterEffectValue( BuyBoosterType.InfiniteShots )
      ?? 5;
    return Math.max( 0.001, seconds ) * 1000;
  }

  private static renderFrame(): void
  {
    if ( !this.params || !this.text_canvas )
      return;

    if ( DialogManager.isAnyDialogOpened() )
    {
      this.hide();
      return;
    }

    if ( this.params.booster_type === BuyBoosterType.InfiniteShots
      && this.countdown_started
      && performance.now() - this.duration_started_at >= this.getInfiniteShotsDurationMs() )
    {
      // Render the actual zero state once before closing on the next frame.
      this.syncLayout();
      this.renderUI();
      this.animation_frame = requestAnimationFrame( () => this.hide() );
      return;
    }

    this.diag.frames++;
    this.syncLayout();
    this.syncDimmingMode();
    this.updateBoosterBump( performance.now() );
    if ( this.needsUIRepaint() )
      this.renderUI();
    if ( this.usesProgressBar() )
      this.renderProgressBar();
    this.animation_frame = requestAnimationFrame( () => this.renderFrame() );
  }

  private static needsUIRepaint(): boolean
  {
    if ( this.legacyUiEveryFrame() )
      return true;

    const game_ui_revision = Game.gameUIPaintRevision();
    if ( game_ui_revision !== this.last_game_ui_revision )
    {
      this.last_game_ui_revision = game_ui_revision;
      this.ui_dirty = true;
    }

    if ( this.booster_bump_started_at !== null && !this.dom_root )
      this.ui_dirty = true;

    if ( this.params?.aim_tutorial_target && !this.dom_root )
      this.ui_dirty = true;

    return this.ui_dirty;
  }

  private static dom_highlight_layer: HTMLDivElement | null = null;
  private static dom_highlight_clones = new Map<BoosterUIHighlight, { clip: HTMLDivElement, revision: number }>();

  private static clearDomHighlights(): void
  {
    this.dom_highlight_clones.clear();
    if ( this.dom_highlight_layer )
      this.dom_highlight_layer.replaceChildren();
  }

  private static syncDomHighlight( highlight: BoosterUIHighlight, bump_scale: number ): void
  {
    const root = highlight.dom_root as HTMLElement;
    if ( !this.dom_highlight_layer )
    {
      const layer = document.createElement( "div" );
      layer.id = "boosterHighlightDomLayer";
      const s = layer.style;
      s.position = "fixed";
      s.left = "0";
      s.top = "0";
      s.width = "0";
      s.height = "0";
      s.overflow = "visible";
      s.zIndex = "125";
      s.pointerEvents = "none";
      document.body.appendChild( layer );
      this.dom_highlight_layer = layer;
    }

    const rect = highlight.client_rect;
    const revision = Game.gameUIPaintRevision();
    let entry = this.dom_highlight_clones.get( highlight );
    if ( !entry )
    {
      const clip = document.createElement( "div" );
      const s = clip.style;
      s.position = "absolute";
      s.overflow = "hidden";
      s.pointerEvents = "none";
      s.transformOrigin = "50% 50%";
      this.dom_highlight_layer.appendChild( clip );
      entry = { clip, revision: -1 };
      this.dom_highlight_clones.set( highlight, entry );
    }

    const clip = entry.clip;
    const clip_style = clip.style;
    clip_style.left = `${rect.x}px`;
    clip_style.top = `${rect.y}px`;
    clip_style.width = `${rect.width}px`;
    clip_style.height = `${rect.height}px`;
    if ( !this.dom_root )
      clip_style.transform = bump_scale !== 1 ? `scale(${bump_scale})` : "";

    if ( entry.revision !== revision )
    {
      entry.revision = revision;
      const root_bounds = root.getBoundingClientRect();
      const clone = root.cloneNode( true ) as HTMLElement;
      const source_canvases = root.querySelectorAll( "canvas" );
      const clone_canvases = clone.querySelectorAll( "canvas" );
      for ( let i = 0; i < clone_canvases.length; i++ )
      {
        const src = source_canvases[i];
        const dst = clone_canvases[i];
        dst.width = src.width;
        dst.height = src.height;
        dst.getContext( "2d" )?.drawImage( src, 0, 0 );
      }
      for ( const dimmed of clone.querySelectorAll( ".ui-dom-dim" ) )
        dimmed.classList.remove( "ui-dom-dim" );
      const cs = clone.style;
      cs.left = `${root_bounds.left - rect.x}px`;
      cs.top = `${root_bounds.top - rect.y}px`;
      cs.translate = "";
      cs.transform = "";
      cs.scale = "";
      cs.rotate = "";
      clip.replaceChildren( clone );
    }
  }

  private static startDomBump(): void
  {
    if ( !this.dom_root || !this.params )
      return;
    for ( const anim of this.dom_bump_anims )
      anim.cancel();
    this.dom_bump_anims = [];
    for ( const highlight of this.params.ui_highlights )
    {
      const entry = this.dom_highlight_clones.get( highlight );
      if ( !entry )
        continue;
      const anim = startDomSampledAnimation( entry.clip, this.BOOSTER_BUMP_DURATION_MS, t =>
        ( { scale: new Vector2( 1 + Math.sin( t * Math.PI ) * this.BOOSTER_BUMP_SCALE, 1 + Math.sin( t * Math.PI ) * this.BOOSTER_BUMP_SCALE ) } ), { samples: 24 } );
      if ( anim )
        this.dom_bump_anims.push( anim );
    }
  }

  private static drawUIHighlights(): void
  {
    if ( !this.params || !this.text_ctx || !this.text_canvas )
      return;

    const destination_bounds = this.text_canvas.getBoundingClientRect();
    const bump_scale = this.getBoosterBumpScale( performance.now() );
    for ( const highlight of this.params.ui_highlights )
    {
      if ( highlight.dom_root )
      {
        this.syncDomHighlight( highlight, bump_scale );
        continue;
      }
      const source_bounds = highlight.canvas.getBoundingClientRect();
      const rect = highlight.client_rect;
      const sx = ( rect.x - source_bounds.left ) / source_bounds.width * highlight.canvas.width;
      const sy = ( rect.y - source_bounds.top ) / source_bounds.height * highlight.canvas.height;
      const sw = rect.width / source_bounds.width * highlight.canvas.width;
      const sh = rect.height / source_bounds.height * highlight.canvas.height;
      const base_dx = ( rect.x - destination_bounds.left ) / destination_bounds.width * Game.screenW();
      const base_dy = ( rect.y - destination_bounds.top ) / destination_bounds.height * Game.screenH();
      const base_dw = rect.width / destination_bounds.width * Game.screenW();
      const base_dh = rect.height / destination_bounds.height * Game.screenH();
      const dw = base_dw * bump_scale;
      const dh = base_dh * bump_scale;
      const dx = base_dx - ( dw - base_dw ) / 2;
      const dy = base_dy - ( dh - base_dh ) / 2;

      this.text_ctx.drawImage( highlight.canvas, sx, sy, sw, sh, dx, dy, dw, dh );
    }
  }

  private static getBoosterBumpScale( now: number ): number
  {
    if ( this.booster_bump_started_at === null )
      return 1;

    const progress = THREE.MathUtils.clamp(
      ( now - this.booster_bump_started_at ) / this.BOOSTER_BUMP_DURATION_MS,
      0,
      1
    );
    if ( progress >= 1 )
      return 1;

    return 1 + Math.sin( progress * Math.PI ) * this.BOOSTER_BUMP_SCALE;
  }

  // The closing bump must survive until hide() runs, otherwise the highlight
  // renderer can stay open forever.
  private static updateBoosterBump( now: number ): void
  {
    if ( this.booster_bump_started_at === null || this.hide_after_booster_bump )
      return;

    if ( now - this.booster_bump_started_at < this.BOOSTER_BUMP_DURATION_MS )
      return;

    this.booster_bump_started_at = null;
    this.ui_dirty = true;
  }

  private static renderUI(): void
  {
    if ( !this.params || !this.text_ctx || !this.title || !this.description )
      return;

    this.ui_dirty = false;
    this.diag.ui++;

    this.text_ctx.clearRect( 0, 0, Game.screenW(), Game.screenH() );
    if ( this.params.booster_type === BuyBoosterType.InfiniteShots
      && this.countdown_started )
      this.drawInfiniteShotsGradient();
    else if ( this.dom_root )
      this.dom_root.getDomHost()!.style.background = "";
    if ( SceneDimmer.isActive() )
    {
      const had_clones = this.dom_highlight_clones.size > 0;
      this.drawUIHighlights();
      if ( !had_clones && this.booster_bump_started_at !== null )
        this.startDomBump();
    }
    else
      this.clearDomHighlights();

    if ( this.dom_root )
    {
      this.dom_root.getDomNode().style.display = "";
      const shown = this.description;
      for ( const d of [ this.standard_description, this.infinite_description ] )
        if ( d ) d.getDomNode().style.display = d === shown ? "" : "none";
      this.dom_root.invalidateSubtree();
      this.dom_root.update( performance.now() );
      this.dom_root.render();
      return;
    }

    this.title.forceMakeShouldBeRendered();
    this.description.forceMakeShouldBeRendered();
    this.title.render();
    this.description.render();

    if ( this.params.aim_tutorial_target && this.aim_tutorial_cursor )
    {
      this.updateAimTutorialCursor( performance.now() );
      this.aim_tutorial_cursor.forceMakeShouldBeRendered();
      this.aim_tutorial_cursor.render();
    }
  }

  private static renderProgressBar(): void
  {
    const base = this.progress_base;
    const fill = this.progress_fill;
    if ( !base || !fill || !this.progress_ctx )
      return;

    const progress = this.countdown_started
      ? THREE.MathUtils.clamp(
        1 - ( performance.now() - this.duration_started_at ) / this.getInfiniteShotsDurationMs(),
        0,
        1
      )
      : 1;
    fill.setShare( progress );

    if ( this.dom_root )
    {
      this.dom_root.update( performance.now() );
      this.dom_root.render();
      return;
    }

    this.progress_ctx.clearRect( 0, 0, this.progress_origin.width, this.progress_origin.height );
    base.forceMakeShouldBeRendered();
    base.render();
    fill.forceMakeShouldBeRendered();
    fill.render();

    this.diag.progress++;
  }

  private static configureAimTutorialCursor(): void
  {
    if ( !this.aim_tutorial_cursor || !this.text_canvas || !this.params?.aim_tutorial_target )
    {
      this.hideAimTutorialCursor();
      return;
    }

    if ( this.hide_after_booster_bump
      && this.booster_bump_started_at !== null
      && performance.now() - this.booster_bump_started_at >= this.BOOSTER_BUMP_DURATION_MS )
    {
      this.hide();
      return;
    }

    const bounds = this.text_canvas.getBoundingClientRect();
    const target = this.params.aim_tutorial_target;
    this.aim_cursor_rest_pos = new Vector2(
      ( target.client_x - bounds.left ) / bounds.width * Game.screenW(),
      ( target.client_y - bounds.top ) / bounds.height * Game.screenH()
    );

    const cursor = this.aim_tutorial_cursor;
    cursor.stopAllTransforms();
    cursor.setScaleImmediate( new Vector2( 1, 1 ) );
    cursor.setPixelPos( this.aim_cursor_rest_pos, true );
    cursor.setOpacityImmediate( 1 );

    if ( this.dom_root )
    {
      this.dom_cursor_anim?.cancel();
      const rest = this.aim_cursor_rest_pos;
      const rect = cursor.getRect();
      const period = this.CURSOR_PULSE_DURATION_MS * 2;
      this.dom_cursor_anim = startDomSampledAnimation( cursor.getDomNode(), period, t =>
      {
        const cycle = t * 2;
        const linear = cycle <= 1 ? cycle : 2 - cycle;
        const eased = linear < 0.5 ? 2 * linear * linear : 1 - Math.pow( -2 * linear + 2, 2 ) / 2;
        const scale = 1 - ( 1 - this.CURSOR_PULSE_SCALE ) * eased;
        return {
          scale: new Vector2( scale, scale ),
          pos: new Vector2( rest.x - ( 1 - scale ) * rect.getW2(), rest.y - ( 1 - scale ) * rect.getH2() ),
        };
      }, { iterations: Infinity, samples: 60 } );
      this.ui_dirty = true;
    }
  }

  private static updateAimTutorialCursor( now: number ): void
  {
    if ( !this.aim_tutorial_cursor || !this.aim_cursor_rest_pos )
      return;

    const cycle = ( now % ( this.CURSOR_PULSE_DURATION_MS * 2 ) )
      / this.CURSOR_PULSE_DURATION_MS;
    const linear = cycle <= 1 ? cycle : 2 - cycle;
    const eased = linear < 0.5
      ? 2 * linear * linear
      : 1 - Math.pow( -2 * linear + 2, 2 ) / 2;
    const scale = 1 - ( 1 - this.CURSOR_PULSE_SCALE ) * eased;
    const rect = this.aim_tutorial_cursor.getRect();

    this.aim_tutorial_cursor.setScaleImmediate( new Vector2( scale, scale ) );
    this.aim_tutorial_cursor.setPixelPos( new Vector2(
      this.aim_cursor_rest_pos.x - ( 1 - scale ) * rect.getW2(),
      this.aim_cursor_rest_pos.y - ( 1 - scale ) * rect.getH2()
    ), true );
  }

  private static hideAimTutorialCursor(): void
  {
    if ( !this.aim_tutorial_cursor )
      return;

    this.aim_tutorial_cursor.stopAllTransforms();
    this.aim_tutorial_cursor.setScaleImmediate( new Vector2( 1, 1 ) );
    if ( this.aim_cursor_rest_pos )
      this.aim_tutorial_cursor.setPixelPos( this.aim_cursor_rest_pos, true );
    this.aim_tutorial_cursor.setOpacityImmediate( 0 );
    this.aim_cursor_rest_pos = null;
    this.dom_cursor_anim?.cancel();
    this.dom_cursor_anim = null;
  }

  private static gradient_cache: CanvasGradient | null = null;
  private static gradient_cache_bottom = -1;

  private static drawInfiniteShotsGradient(): void
  {
    if ( !this.text_ctx )
      return;

    const gradient_bottom = Game.safeAreaTop()
      + LayoutScaler.heightFromDesign( this.INFINITE_GRADIENT_HEIGHT );

    if ( !this.gradient_cache || this.gradient_cache_bottom !== gradient_bottom )
    {
      const gradient = this.text_ctx.createLinearGradient( 0, 0, 0, gradient_bottom );
      gradient.addColorStop( 0, `rgba(0, 0, 0, ${this.INFINITE_GRADIENT_OPACITY})` );
      gradient.addColorStop( 0.45, "rgba(0, 0, 0, 0.5)" );
      gradient.addColorStop( 1, "rgba(0, 0, 0, 0)" );
      this.gradient_cache = gradient;
      this.gradient_cache_bottom = gradient_bottom;
    }

    if ( this.dom_root )
    {
      const host = this.dom_root.getDomHost();
      if ( host )
        host.style.background = `linear-gradient(rgba(0, 0, 0, ${this.INFINITE_GRADIENT_OPACITY}) 0px, rgba(0, 0, 0, 0.5) ${gradient_bottom * 0.45}px, rgba(0, 0, 0, 0) ${gradient_bottom}px) top / 100% ${gradient_bottom}px no-repeat`;
      return;
    }

    this.text_ctx.save();
    this.text_ctx.fillStyle = this.gradient_cache;
    this.text_ctx.fillRect( 0, 0, Game.screenW(), gradient_bottom );
    this.text_ctx.restore();
  }

  private static usesFullSceneDimming(): boolean
  {
    return this.params?.booster_type !== BuyBoosterType.InfiniteShots
      || !this.countdown_started;
  }

  private static usesProgressBar(): boolean
  {
    return this.params?.booster_type === BuyBoosterType.InfiniteShots
      && this.progress_fill !== null;
  }

  private static syncDimmingMode(): void
  {
    const use_full_scene_dimming = this.usesFullSceneDimming();
    if ( use_full_scene_dimming === SceneDimmer.isActive() )
      return;

    this.params?.on_scene_dimming( use_full_scene_dimming );
    if ( !use_full_scene_dimming )
      SceneDimmer.disable();

    this.ui_dirty = true;
  }

  private static syncLayout(): void
  {
    if ( !this.text_canvas || !this.text_ctx )
      return;

    const source = Renderer.renderer.domElement;
    const width = Number.parseFloat( source.style.width ) || Game.screenW();
    const height = Number.parseFloat( source.style.height ) || Game.screenH();
    const pixel_width = Math.round( width * Game.getDpr() );
    const pixel_height = Math.round( height * Game.getDpr() );

    if ( this.text_canvas.width !== pixel_width || this.text_canvas.height !== pixel_height )
    {
      this.text_canvas.width = pixel_width;
      this.text_canvas.height = pixel_height;
      this.ui_dirty = true;
    }

    const layout_key = `${source.style.left}|${source.style.top}|${source.style.width}|${source.style.height}`;
    if ( layout_key !== this.last_layout_key )
    {
      this.last_layout_key = layout_key;
      this.ui_dirty = true;
    }
    this.text_ctx.setTransform( Game.getDpr(), 0, 0, Game.getDpr(), 0, 0 );
    this.text_canvas.style.left = source.style.left;
    this.text_canvas.style.top = source.style.top;
    this.text_canvas.style.width = source.style.width;
    this.text_canvas.style.height = source.style.height;

    this.syncProgressLayout( source, width, height );
  }

  private static syncProgressLayout(
    source: HTMLCanvasElement,
    css_width: number,
    css_height: number
  ): void
  {
    if ( !this.progress_canvas || !this.progress_ctx )
      return;

    const scale_x = css_width / Game.screenW();
    const scale_y = css_height / Game.screenH();
    const dpr = Game.getDpr();

    const pixel_width = Math.round( this.progress_origin.width * scale_x * dpr );
    const pixel_height = Math.round( this.progress_origin.height * scale_y * dpr );

    if ( this.progress_canvas.width !== pixel_width || this.progress_canvas.height !== pixel_height )
    {
      this.progress_canvas.width = pixel_width;
      this.progress_canvas.height = pixel_height;
    }

    this.progress_ctx.setTransform( scale_x * dpr, 0, 0, scale_y * dpr, 0, 0 );

    const left = Number.parseFloat( source.style.left ) || 0;
    const top = Number.parseFloat( source.style.top ) || 0;
    this.progress_canvas.style.left = `${left + this.progress_origin.x * scale_x}px`;
    this.progress_canvas.style.top = `${top + this.progress_origin.y * scale_y}px`;
    this.progress_canvas.style.width = `${this.progress_origin.width * scale_x}px`;
    this.progress_canvas.style.height = `${this.progress_origin.height * scale_y}px`;
  }
}
